import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createBanditSession,
    deleteBanditSession,
    moveBanditSession,
    SPIN_ENGAGEMENT,
    PAYOUT_SCORE_SCALE,
    INITIAL_CREDITS,
    spinScoreGain,
} from './banditGmClient';
import { useTransientMessage } from '../useTransientMessage';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'one_armed_bandit';
const { UPLOAD_URL } = ARCADE.one_armed_bandit;
const ACCENT = GAME_ACCENT.one_armed_bandit;
const HINT = 'Space / Enter spin · ↑↓ / [ ] change bet · Esc pause';

export { SPIN_ENGAGEMENT, PAYOUT_SCORE_SCALE, spinScoreGain };

const OneArmedBandit = () => {
    const play = useManualPlay();
    const sessionIdRef = useRef(null);
    const [credits, setCredits] = useState(INITIAL_CREDITS);
    const [score, setScore] = useState(0);
    const [bet, setBet] = useState(1);
    const [totalSpins, setTotalSpins] = useState(0);
    const [lastSpin, setLastSpin] = useState(null);
    const [isGameOver, setIsGameOver] = useState(false);
    const [spinning, setSpinning] = useState(false);
    const [message, setMessage] = useTransientMessage();

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });

    const applyGm = useCallback((data) => {
        if (typeof data.credits === 'number') setCredits(data.credits);
        if (typeof data.score === 'number') setScore(data.score);
        if (typeof data.total_spins === 'number') setTotalSpins(data.total_spins);
        if (data.last_spin) setLastSpin(data.last_spin);
        setIsGameOver(Boolean(data.is_game_over));
        setMessage(data.message || '');
        return {
            over: Boolean(data.is_game_over),
            score: typeof data.score === 'number' ? data.score : 0,
            credits: typeof data.credits === 'number' ? data.credits : 0,
            spins: typeof data.total_spins === 'number' ? data.total_spins : 0,
        };
    }, [setMessage]);

    const initFromGm = useCallback(async () => {
        if (sessionIdRef.current) {
            await deleteBanditSession(sessionIdRef.current);
            sessionIdRef.current = null;
        }
        const data = await createBanditSession();
        if (!data?.session_id) throw new Error('Bandit GM missing session_id');
        sessionIdRef.current = data.session_id;
        applyGm(data);
        setBet(1);
        setLastSpin(null);
        recorder.reset({ credits: INITIAL_CREDITS });
    }, [applyGm, recorder]);

    const startGame = useCallback(async () => {
        try {
            setSpinning(true);
            await initFromGm();
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            setSpinning(false);
        }
    }, [initFromGm, play, setMessage]);

    const endGame = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        await deleteBanditSession(sid);
        play.endGame({ score });
        setCredits(INITIAL_CREDITS);
        setScore(0);
        setBet(1);
        setTotalSpins(0);
        setLastSpin(null);
        setIsGameOver(false);
        setSpinning(false);
        setMessage('');
    }, [play, setMessage, score]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteBanditSession(sid);
    }, []);

    const spin = useCallback(async () => {
        if (play.phase !== 'playing' || isGameOver || spinning) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        if (bet <= 0 || bet > credits) {
            setMessage('Insufficient credits for that bet.');
            return;
        }
        setSpinning(true);
        try {
            // Brief UX delay; result comes from GM (authoritative RNG).
            await new Promise((r) => setTimeout(r, 280));
            const data = await moveBanditSession(sid, { bet });
            const meta = applyGm(data);
            const summary = data.last_spin || {};
            recorder.recordTick({
                reels: summary.reels,
                bet: summary.bet ?? bet,
                payout: summary.payout,
                net: summary.net,
                credits_after: meta.credits,
                score: meta.score,
            });
            if (meta.over) {
                play.markOver();
                recorder.finalizeAndUpload({
                    score: meta.score,
                    extra: { credits: meta.credits, spins: meta.spins, score: meta.score },
                });
                const done = sessionIdRef.current;
                sessionIdRef.current = null;
                deleteBanditSession(done);
            }
        } catch (err) {
            console.warn('Bandit GM spin failed', err);
            setMessage(err?.data?.message || 'Spin failed');
        } finally {
            setSpinning(false);
        }
    }, [applyGm, bet, credits, isGameOver, play, recorder, spinning, setMessage]);

    const onKey = useCallback((e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            spin();
            e.preventDefault();
        } else if (['ArrowUp', ']', 'w', 'W'].includes(e.key)) {
            setBet((b) => Math.min(credits, b + 1));
            e.preventDefault();
        } else if (['ArrowDown', '[', 's', 'S'].includes(e.key)) {
            setBet((b) => Math.max(1, b - 1));
            e.preventDefault();
        }
    }, [credits, spin]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [spin, credits],
    });

    return (
        <ArcadeGameShell
            title="One-Armed"
            titleAccent="Bandit"
            subtitle="Pull the lever manually or upload a betting agent"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.one_armed_bandit}
            session={session}
            formatScore={(e) => String(e.score ?? e.credits ?? 0)}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                keyboardStart={false}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Credits: ${credits}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`Score: ${score}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Bet: ${bet}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Spins: ${totalSpins}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={isGameOver ? 'Locked' : 'Ready'} size="small" color={isGameOver ? 'warning' : 'success'} />
                    </Stack>
                    {message && <Typography sx={{ color: COLOR.textMuted, mb: 2, fontSize: '0.85rem' }}>{message}</Typography>}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 2,
                        justifyItems: 'center',
                        p: 3,
                        borderRadius: 3,
                        border: '2px solid rgba(255,255,255,0.12)',
                        background: 'linear-gradient(135deg, rgba(255,180,0,0.08), rgba(255,255,255,0.04))',
                        opacity: spinning ? 0.6 : 1,
                        transition: 'opacity 0.2s',
                    }}
                    >
                        {(lastSpin?.reels || ['—', '—', '—']).map((symbol, idx) => (
                            <Typography key={idx} variant="h4" sx={{ color: ACCENT, opacity: lastSpin ? 1 : 0.35 }}>
                                {symbol}
                            </Typography>
                        ))}
                    </Box>
                    {lastSpin && (
                        <Typography sx={{ color: '#888', mt: 1.5, textAlign: 'center', fontSize: '0.85rem' }}>
                            Payout {Number(lastSpin.payout).toFixed(0)} · Net {lastSpin.net >= 0 ? '+' : ''}{Number(lastSpin.net).toFixed(0)}
                        </Typography>
                    )}
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default OneArmedBandit;
